import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/SossietyDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { InstructorAutocomplete } from '@/components/ui/InstructorAutocomplete';
import { AcademyAutocomplete } from '@/components/ui/AcademyAutocomplete';
import { StudentDisplay } from '@/components/ui/StudentDisplay';
import { CheckCircle2 } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { useI18n } from '@/contexts/I18nContext';
import { useQueryClient } from '@tanstack/react-query';
import { useAgendaConfig } from '@/hooks/useAgendaConfig';
import { createUTCDateTime, formatDateTimeForDisplay } from '@/utils/timezoneUtils';

// Time options in 30-minute intervals
const TIME_OPTIONS = [
  '12:00 AM', '12:30 AM',
  '1:00 AM', '1:30 AM',
  '2:00 AM', '2:30 AM',
  '3:00 AM', '3:30 AM',
  '4:00 AM', '4:30 AM',
  '5:00 AM', '5:30 AM',
  '6:00 AM', '6:30 AM',
  '7:00 AM', '7:30 AM',
  '8:00 AM', '8:30 AM',
  '9:00 AM', '9:30 AM',
  '10:00 AM', '10:30 AM',
  '11:00 AM', '11:30 AM',
  '12:00 PM', '12:30 PM',
  '1:00 PM', '1:30 PM',
  '2:00 PM', '2:30 PM',
  '3:00 PM', '3:30 PM',
  '4:00 PM', '4:30 PM',
  '5:00 PM', '5:30 PM',
  '6:00 PM', '6:30 PM',
  '7:00 PM', '7:30 PM',
  '8:00 PM', '8:30 PM',
  '9:00 PM', '9:30 PM',
  '10:00 PM', '10:30 PM',
  '11:00 PM', '11:30 PM'
];

// Helper function to format time input to AM/PM format
const formatTimeToAMPM = (timeInput: string): string | null => {
  if (!timeInput) return null;
  
  // If already in AM/PM format, return as is
  if (timeInput.includes('AM') || timeInput.includes('PM')) {
    return timeInput;
  }
  
  // Try to parse as 24-hour format
  const timeRegex = /^(\d{1,2}):(\d{2})$/;
  const match = timeInput.match(timeRegex);
  
  if (match) {
    const hours = parseInt(match[1]);
    const minutes = parseInt(match[2]);
    
    if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
      return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
    }
  }
  
  return null;
};


// Helper function to validate if class time is within agenda configuration
const validateClassTime = (date: string, startTime: string, duration: number, agendaConfig: any): { isValid: boolean; message?: string } => {
  if (!agendaConfig || !agendaConfig.working_days || !agendaConfig.working_hours) {
    return { isValid: true }; // If no config, allow any time
  }

  // Get day of week
  const classDate = new Date(date);
  const dayOfWeek = classDate.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase();
  
  // Check if it's a working day
  if (!agendaConfig.working_days.includes(dayOfWeek)) {
    return { 
      isValid: false, 
      message: `Classes cannot be scheduled on ${dayOfWeek}. Please choose a working day.` 
    };
  }

  // Get working hours for this day
  const workingHours = agendaConfig.working_hours[dayOfWeek];
  if (!workingHours) {
    return { isValid: true }; // If no hours set for this day, allow any time
  }

  // Convert times to 24-hour format for comparison using central utility
  const startDateTimeISO = createUTCDateTime(date, startTime);
  const startDateTime = new Date(startDateTimeISO);
  const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
  
  // Convert working hours to local time for comparison
  const [openHour, openMinute] = workingHours.start.split(':').map(Number);
  const [closeHour, closeMinute] = workingHours.end.split(':').map(Number);
  
  // Create working hours in local time
  const workingDate = new Date(date + 'T00:00:00');
  const openDateTime = new Date(workingDate);
  openDateTime.setHours(openHour, openMinute, 0, 0);
  const closeDateTime = new Date(workingDate);
  closeDateTime.setHours(closeHour, closeMinute, 0, 0);

  // Convert class times to local time for comparison
  const classStartLocal = new Date(startDateTime.getTime() - (startDateTime.getTimezoneOffset() * 60000));
  const classEndLocal = new Date(endDateTime.getTime() - (endDateTime.getTimezoneOffset() * 60000));

  // Convert to minutes for easier comparison (all in local time)
  const classStartMinutes = classStartLocal.getHours() * 60 + classStartLocal.getMinutes();
  const classEndMinutes = classEndLocal.getHours() * 60 + classEndLocal.getMinutes();
  const openMinutes = openDateTime.getHours() * 60 + openDateTime.getMinutes();
  const closeMinutes = closeDateTime.getHours() * 60 + closeDateTime.getMinutes();

  // Check if class is within working hours (warning only, not blocking)
  const isOutsideWorkingHours = classStartMinutes < openMinutes || classEndMinutes > closeMinutes;
  
  if (isOutsideWorkingHours) {
    const openTimeFormatted = `${openHour.toString().padStart(2, '0')}:${openMinute.toString().padStart(2, '0')}`;
    const closeTimeFormatted = `${closeHour.toString().padStart(2, '0')}:${closeMinute.toString().padStart(2, '0')}`;
    
    return { 
      isValid: true, // Allow creation but with warning
      message: `⚠️ Class time is outside your working hours (${openTimeFormatted} - ${closeTimeFormatted}). This class will not affect your available slots.` 
    };
  }

  return { isValid: true };
};

// Helper function to get location name
const getLocationName = (location: string | { id: string; name: string }): string => {
  if (typeof location === 'string') return location;
  if (location && typeof location === 'object') return location.name;
  return '';
};

interface Student {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface AddClassModalProps {
  isOpen: boolean;
  onClose: () => void;
  onClassCreated?: (classData: any) => void;
  // Edit mode props
  mode?: 'create' | 'edit';
  classToEdit?: any;
  onClassUpdated?: () => void;
}

export const AddClassModal: React.FC<AddClassModalProps> = ({
  isOpen,
  onClose,
  onClassCreated,
  mode = 'create',
  classToEdit,
  onClassUpdated
}) => {
  const { t } = useI18n();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { config: agendaConfig } = useAgendaConfig();
  const [isLoading, setIsLoading] = useState(false);
  const [timeWarning, setTimeWarning] = useState<string>('');

  // Constants
  const CLASS_CATEGORIES = [
    'Conditioning',
    'Rolling',
    'Open Mat',
    'Drills',
    'Competition Prep',
    'Other'
  ];
  const [classType, setClassType] = useState<string>(t('calendar.group_class'));
  const [modality, setModality] = useState<'Gi' | 'No-Gi'>('Gi');
  const [category, setCategory] = useState<string>('');
  const [location, setLocation] = useState<string | { id: string; name: string }>('');
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [duration, setDuration] = useState(60);
  const [description, setDescription] = useState('');
  const [students, setStudents] = useState<Student[]>([]);
  const [selectedStudent, setSelectedStudent] = useState<Student | string | null>(null);
  const [step, setStep] = useState<'form' | 'success'>('form');
  const [createdClass, setCreatedClass] = useState<any>(null);

  // Load user's academy on component mount
  useEffect(() => {
    const loadUserAcademy = async () => {
      if (user?.id) {
        try {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('main_academy_id')
            .eq('id', user.id)
            .single();
          
          if (profileData?.main_academy_id) {
            const { data: academyData } = await supabase
              .from('academies')
              .select('name')
              .eq('id', profileData.main_academy_id)
              .single();
            
            if (academyData?.name) {
              setLocation({ id: profileData.main_academy_id, name: academyData.name });
            }
          }
        } catch (error) {
          console.error('Error loading user academy:', error);
        }
      }
    };

    loadUserAcademy();
  }, [user?.id]);

  // Load class data when in edit mode
  React.useEffect(() => {
    if (mode === 'edit' && classToEdit) {
      setClassType(classToEdit.type || 'Group Class');
      setCategory(classToEdit.category || '');
      setModality(classToEdit.modality || 'Gi');
      setLocation(classToEdit.academy_name || '');
      
      // Use central utility to format datetime for display
      const { date: displayDate, time: displayTime } = formatDateTimeForDisplay(classToEdit.start_time);
      setDate(displayDate);
      setStartTime(displayTime);
      
      setDuration(Math.round((new Date(classToEdit.end_time).getTime() - new Date(classToEdit.start_time).getTime()) / 60000));
      setDescription(classToEdit.description || '');
      
      // Load students if it's a Private Class
      if (classToEdit.type === 'Private Class' && classToEdit.training_partners) {
        setStudents(classToEdit.training_partners.map((partner: any) => ({
          id: partner.partner_user_id,
          full_name: partner.profiles?.full_name || 'Unknown Student',
          username: partner.profiles?.username || 'unknown',
          profile_picture_url: partner.profiles?.profile_picture_url,
          belt_rank: partner.profiles?.belt_rank
        })));
      }
    }
  }, [mode, classToEdit]);


  // Validate time in real-time when date, time, or duration changes
  useEffect(() => {
    if (date && startTime && duration && agendaConfig) {
      const validation = validateClassTime(date, startTime, duration, agendaConfig);
      if (!validation.isValid) {
        setTimeWarning(validation.message || '');
      } else {
        setTimeWarning('');
      }
    } else {
      setTimeWarning('');
    }
  }, [date, startTime, duration, agendaConfig]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user?.id) {
      toast({
        title: "Error",
        description: "You must be logged in to create a class.",
        variant: "destructive",
      });
      return;
    }

    if (!classType || !modality || !date || !startTime) {
      toast({
        title: "Error",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }


    // Validate class time against agenda configuration
    const timeValidation = validateClassTime(date, startTime, duration, agendaConfig);
    if (!timeValidation.isValid) {
      toast({
        title: "Invalid Schedule",
        description: timeValidation.message,
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Use central timezone utility to create local datetime
      const startDateTimeISO = createUTCDateTime(date, startTime);
      const startDateTime = new Date(startDateTimeISO);
      const endDateTime = new Date(startDateTime.getTime() + duration * 60000);
      
      let classData;
      
      if (mode === 'edit' && classToEdit) {
        // Update existing class
        const { data, error: classError } = await supabase
          .from('trainings')
          .update({
            title: classType,
            type: classType,
            category: category || null,
            modality,
            academy_name: getLocationName(location) || null,
            start_time: startDateTimeISO,
            end_time: endDateTime.toISOString(),
            is_class: true
          })
          .eq('id', classToEdit.id)
          .select()
          .single();

        if (classError) {
          throw classError;
        }
        
        classData = data;
        
        // Update students for Private Class
        if (classType === 'Private Class') {
          // Remove existing students
          await supabase
            .from('training_partners')
            .delete()
            .eq('training_id', classToEdit.id);
          
          // Add new students
          if (students.length > 0) {
            const studentInserts = students
              .filter(student => student.username !== 'manual')
              .map(student => ({
                training_id: classToEdit.id,
                partner_user_id: student.id
              }));

            if (studentInserts.length > 0) {
              const { error: studentsError } = await supabase
                .from('training_partners')
                .insert(studentInserts);

              if (studentsError) {
                console.error('Error updating students:', studentsError);
              }
            }
          }
        } else {
          // Remove all students for Group Class
          await supabase
            .from('training_partners')
            .delete()
            .eq('training_id', classToEdit.id);
        }
        
        onClassUpdated?.();
      } else {
        // Create new class
        
        const { data, error: classError } = await supabase
          .from('trainings')
          .insert({
            user_id: user.id,
            title: classType,
            type: classType,
            category: category || null,
            start_time: startDateTimeISO,
            end_time: endDateTime.toISOString(),
            modality,
            academy_name: getLocationName(location) || null,
            status: t('calendar.scheduled'),
            is_class: true
          })
          .select()
          .single();

        if (classError) {
          throw classError;
        }
        
        classData = data;

        // Add students if it's a Private Class and students are provided
        if (classType === 'Private Class' && students.length > 0) {
          const studentInserts = students.map(student => {
            if (student.username === 'manual') {
              // Manual student - save with manual_partner_name
              return {
                training_id: classData.id,
                manual_partner_name: student.full_name
              };
            } else {
              // Regular student - save with partner_user_id
              return {
                training_id: classData.id,
                partner_user_id: student.id
              };
            }
          });

          if (studentInserts.length > 0) {
            const { error: studentsError } = await supabase
              .from('training_partners')
              .insert(studentInserts);

            if (studentsError) {
              console.error('Error adding students:', studentsError);
            }
          }
        }
        
        if (mode === 'edit') {
          // In edit mode, close modal directly and call update callback
          onClassUpdated?.();
          onClose();
        } else {
          // In create mode, show success modal
          if (mode === 'create') {
            setCreatedClass(classData);
            setStep('success');
          }
          onClassCreated?.(classData);
        }
      }

    } catch (error) {
      console.error('Error saving class:', error);
      toast({
        title: "Error",
        description: mode === 'edit' ? "Failed to update class. Please try again." : "Failed to create class. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      // Only reset form fields if not in edit mode
      if (mode === 'create') {
        setClassType('Group Class');
        setCategory('');
        setModality('Gi');
        setLocation('');
        setDate('');
        setStartTime('');
        setDuration(60);
        setDescription('');
        setStudents([]);
        setSelectedStudent(null);
      }
      setStep('form');
      setCreatedClass(null);
      onClose();
    }
  };

  // Handle class type change - reset students when switching to Group Class
  const handleClassTypeChange = (type: 'Group Class' | 'Private Class') => {
    setClassType(type);
    if (type === 'Group Class') {
      setStudents([]);
      setSelectedStudent(null);
    }
  };

  // Handle student selection - automatically add when selected
  const handleStudentChange = (student: Student | string | null) => {
    if (student && classType === 'Private Class') {
      if (typeof student === 'string') {
        // Manual student
        const newStudent: Student = {
          id: `manual-${Date.now()}`,
          full_name: student,
          username: 'manual'
        };
        // Check if student is already in the list
        const isAlreadyAdded = students.some(s => s.full_name === student);
        if (!isAlreadyAdded) {
          setStudents(prev => [...prev, newStudent]);
        }
      } else {
        // Check if student is already in the list
        const isAlreadyAdded = students.some(s => s.id === student.id);
        if (!isAlreadyAdded) {
          setStudents(prev => [...prev, student]);
        }
      }
      // Clear the field after adding
      setSelectedStudent(null);
    } else {
      setSelectedStudent(student);
    }
  };

  // Remove student from the list
  const handleRemoveStudent = (studentToRemove: Student) => {
    setStudents(prev => prev.filter(student => student.id !== studentToRemove.id));
  };

  // Success modal - Only show in create mode
  if (step === 'success' && createdClass && mode === 'create') {
    return (
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent className="w-[90vw] max-w-xl bg-white rounded-2xl p-4">
          <div className="flex flex-col items-center justify-center text-center px-1 py-6">
            {/* Title */}
            <h3 className="text-xl font-semibold text-black mb-6 font-roboto">
              Class Added!
            </h3>
            
            {/* Icon */}
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-green-600" />
            </div>
            
            {/* Content */}
            <div className="space-y-4 mb-8">
              <p className="text-gray-600">
                Your class of {format(new Date(createdClass.start_time), 'MMMM d, yyyy')} was successfully added!
              </p>

              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <div className="font-semibold text-black">
                  {createdClass.title}
                </div>
                <div className="text-gray-600">
                  {format(new Date(createdClass.start_time), 'MMMM d, yyyy')} at {format(new Date(createdClass.start_time), 'h:mm a')}
                </div>
                <div className="text-gray-500 text-xs font-medium">
                  {createdClass.modality}
                </div>
              </div>
            </div>

            {/* Button */}
            <Button
              onClick={handleClose}
              className="w-full bg-[#D72E16] hover:bg-[#B8251A] text-white font-medium py-3 h-11 rounded-full transition-colors duration-200"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="bg-white border-gray-300 rounded-2xl w-[90vw] max-w-md"
        aria-describedby="add-class-description"
      >
        <DialogDescription id="add-class-description" className="sr-only">
          {mode === 'edit' ? 'Edit your class details' : 'Add a new class to your schedule'}
        </DialogDescription>
        <SossietyDialogHeader title={mode === 'edit' ? t('calendar.edit_class') : t('calendar.add_class')}>
          <SossietyDialogClose onClick={handleClose} />
        </SossietyDialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type of Class - Toggle */}
          <div>
            <Label className="text-black">Type of Class*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                onClick={() => handleClassTypeChange('Group Class')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  classType === 'Group Class'
                    ? 'bg-[#D72E16] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {t('calendar.group_class')}
              </button>
              <button
                type="button"
                onClick={() => handleClassTypeChange('Private Class')}
                className={`flex-1 py-2 px-4 rounded-lg font-medium transition-colors ${
                  classType === 'Private Class'
                    ? 'bg-[#D72E16] text-white'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {t('calendar.private_class')}
              </button>
            </div>
          </div>

          {/* Modality - Gi/No-Gi Toggle */}
          <div>
            <Label className="text-black">Modality*</Label>
            <div className="flex bg-gray-100 rounded-lg p-1 w-full">
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  modality === 'Gi'
                    ? 'bg-[#D72E16] text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setModality('Gi')}
              >
                Gi
              </button>
              <button
                type="button"
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  modality === 'No-Gi'
                    ? 'bg-[#D72E16] text-white'
                    : 'text-gray-600 hover:text-gray-800'
                }`}
                onClick={() => setModality('No-Gi')}
              >
                No-Gi
              </button>
            </div>
          </div>

          {/* Category */}
          <div>
            <Label className="text-black">Category (Optional)</Label>
            <div className="w-full border border-gray-300 rounded-md">
              <Select 
                value={category} 
                onValueChange={(value) => setCategory(value)}
              >
                <SelectTrigger className="w-full text-black !ring-0 !outline-none !shadow-none border-0 focus:!ring-0 focus:!outline-none focus:!shadow-none bg-transparent relative [&_[data-radix-select-icon]]:hidden text-sm">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-black border-0 py-2 space-y-1 max-h-60">
                  {CLASS_CATEGORIES.map((categoryOption) => (
                    <SelectItem key={categoryOption} value={categoryOption} className="text-white text-sm">
                      {categoryOption}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Location */}
          <div>
            <Label className="text-black">Location</Label>
            <AcademyAutocomplete
              value={location}
              onChange={setLocation}
              placeholder="Search for academy or enter location..."
              className="text-black border-gray-300 bg-white"
              containerClassName="w-full"
            />
          </div>

          {/* Students - Only show for Private Class */}
          {classType === 'Private Class' && (
            <div>
              <Label className="text-black">{t('calendar.students')}</Label>
              <div className="flex-1">
                <InstructorAutocomplete
                  value={selectedStudent}
                  onChange={handleStudentChange}
                  placeholder={t('calendar.search_students_placeholder')}
                  className="text-black border-gray-300 bg-white"
                  containerClassName="w-full"
                />
              </div>
              
              {/* Selected Students List */}
              {students.length > 0 && (
                <div className="mt-3 space-y-2">
                  {students.map((student) => (
                    <StudentDisplay
                      key={student.id}
                      student={{
                        id: student.id,
                        name: student.full_name,
                        username: student.username,
                        profile_picture_url: student.profile_picture_url,
                        belt_rank: student.belt_rank
                      }}
                      showRemoveButton={true}
                      onRemove={() => handleRemoveStudent(student)}
                      size="md"
                      className="p-2 rounded-lg"
                    />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Date and Time */}
          <div className="grid grid-cols-2 gap-2">
            <div className="w-full">
              <Label htmlFor="date" className="text-black font-medium">Date *</Label>
              <Input
                id="date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="text-black border-gray-300 bg-white"
                labelColor="#000"
                required
              />
            </div>
            <div className="w-full">
              <Label htmlFor="startTime" className="text-black font-medium">Start Time *</Label>
              <div className="relative w-full">
                <Input
                  id="startTime"
                  type="text"
                  value={startTime} 
                  onChange={(e) => {
                    let value = e.target.value;
                    setStartTime(value);
                  }}
                  onBlur={(e) => {
                    const value = e.target.value;
                    const formatted = formatTimeToAMPM(value);
                    if (formatted) {
                      setStartTime(formatted);
                    }
                  }}
                  className="text-black border-gray-300 bg-white pr-10"
                  labelColor="#000"
                  list="time-options-start"
                  autoComplete="off"
                  required
                />
                <datalist id="time-options-start">
                  {TIME_OPTIONS.map((time) => (
                    <option key={time} value={time} />
                  ))}
                </datalist>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">
                  AM/PM
                </div>
              </div>
            </div>
          </div>

          {/* Duration */}
          <div>
            <Label htmlFor="duration" className="text-black font-medium">Duration (minutes)*</Label>
            <Input
              id="duration"
              type="number"
              value={duration || ''}
              onChange={(e) => setDuration(Number(e.target.value))}
              className="text-black border-gray-300 bg-white mt-1"
              min="15"
              max="480"
              step="15"
              labelColor="#000"
              required
            />
            {timeWarning && (
              <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-800">{timeWarning}</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Description - Removed as per specification */}
          


          {/* Action Buttons */}
          <div className="flex gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 border-black text-black hover:bg-gray-50 h-12 rounded-full"
            >
              Close
            </Button>
            <Button
              type="submit"
              disabled={isLoading}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-full h-12"
            >
              {isLoading 
                ? (mode === 'edit' ? 'Updating...' : 'Creating...') 
                : (mode === 'edit' ? 'Update Class' : 'Create')
              }
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
